import { Fragment, useEffect, useMemo, useState } from "react";
import { History, Loader2, X, FileSearch, ChevronDown, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import { ACTION_LABEL, ACTION_TONE, KIND_LABEL, summarize, fmtWaktu } from "@/lib/activityHumanize";

/**
 * RiwayatAktivitasTab — 2026-06-20 (user request rework + penyempurnaan):
 * - Kolom: Waktu · Pengguna · Sumber · Tindakan · Ringkasan Perubahan.
 * - Seluruh label teknis diterjemahkan ke Bahasa Indonesia; TIDAK ADA JSON mentah
 *   atau nama field internal di tampilan utama.
 * - Perubahan daftar (mis. sph_submissions) diringkas jadi kalimat:
 *   "Menambahkan SPH versi 2 tanggal 5 Sep 2026 dengan status Disetujui".
 * - Format konsisten "nilai lama → nilai baru". Perubahan panjang → ringkasan +
 *   tombol "Lihat Detail" (dialog kalimat lengkap; JSON teknis hanya untuk admin).
 * - Filter tindakan/pengguna/tanggal + pagination default 20.
 * - READ-ONLY: record audit lama tidak diubah — murni penerjemahan & penyajian.
 * 2026-06-21: seluruh humanizer dipusatkan di `lib/activityHumanize.js`
 * (dipakai juga kartu "Aktivitas Terbaru" Ringkasan) + label field dilengkapi
 * (po_date → "Tanggal PO", work_description → "Deskripsi Pekerjaan", dll).
 */

export default function RiwayatAktivitasTab({ mon, project }) {
  const pid = project?.id;
  const { user } = useAuth();
  const { canPerm } = useAccess();
  // Detail teknis (JSON mentah) hanya untuk admin (BOD Lv1 / kontrol akses).
  const isAdminViewer = canPerm(user, "access_control", "view");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [fAction, setFAction] = useState("");
  const [fActor, setFActor] = useState("");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [detailItem, setDetailItem] = useState(null);

  useEffect(() => {
    if (!pid) return;
    let alive = true;
    setLoading(true);
    const qs = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (fAction) qs.set("action", fAction);
    if (fActor) qs.set("actor", fActor);
    if (fFrom) qs.set("date_from", fFrom);
    if (fTo) qs.set("date_to", fTo);
    api.get(`/active-projects/${pid}/activity?${qs.toString()}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData({ items: [], total: 0, facets: { actions: [], actors: [] } }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [pid, page, pageSize, fAction, fActor, fFrom, fTo]);

  const items = data?.items || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const facets = data?.facets || { actions: [], actors: [] };
  const hasFilter = fAction || fActor || fFrom || fTo;
  const resetFilters = () => { setFAction(""); setFActor(""); setFFrom(""); setFTo(""); setPage(1); };

  // 2026-06-14 (user request): PENGELOMPOKAN aktivitas massal — entri dengan
  // pengguna + aksi + field + nilai baru yang sama dan jeda waktu ≤ 10 menit
  // (berurutan) digabung jadi SATU baris ringkasan, mis. "20 record · Kategori
  // WO: Urgent Case → Non-Urgent Case". Audit asli TIDAK dihapus/diubah —
  // detail tiap record tetap bisa dibuka via expand + tombol "Lihat Detail".
  const GROUP_GAP_MS = 10 * 60 * 1000;
  const grouped = useMemo(() => {
    const sig = (a) => [a.actor_name || "", a.action || "", a.field || "", JSON.stringify(a.new_value ?? null)].join("|");
    const out = [];
    for (const a of items) {
      const ts = new Date(a._ts || a.at || a.created_at || 0).getTime();
      const last = out[out.length - 1];
      if (last && last.sig === sig(a) && Number.isFinite(ts) && Math.abs(last.lastTs - ts) <= GROUP_GAP_MS) {
        last.items.push(a);
        last.lastTs = ts;
      } else {
        out.push({ sig: sig(a), items: [a], lastTs: ts });
      }
    }
    return out;
  }, [items]); // eslint-disable-line
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const toggleGroup = (key) => setExpandedGroups((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const renderActivityRow = (a, indented = false) => {
    const sum = summarize(a);
    const isLong = sum.lines.length > 1 || sum.short.length > 180;
    return (
      <tr key={a.id} data-testid={`act-row-${a.id}`} className="border-b border-slate-100 hover:bg-slate-50">
        <td className={`px-3 py-2 tabular-nums text-slate-500 whitespace-nowrap ${indented ? "pl-7" : ""}`} data-testid={`act-waktu-${a.id}`}>{fmtWaktu(a._ts || a.at || a.created_at)}</td>
        <td className="px-3 py-2 text-slate-700">{a.actor_name || "—"}</td>
        <td className="px-3 py-2 text-slate-600">{KIND_LABEL[a.kind] || KIND_LABEL[a.category] || a.source || "Project"}</td>
        <td className="px-3 py-2" title={a.action || ""}>
          <span className={`inline-block px-1.5 py-0.5 text-[9px] font-semibold border ${ACTION_TONE[a.action] || "bg-sky-50 border-sky-300 text-sky-700"}`} data-testid={`act-tindakan-${a.id}`}>
            {ACTION_LABEL[a.action] || a.action || "—"}
          </span>
        </td>
        <td className="px-3 py-2 text-slate-600 max-w-[380px]" data-testid={`act-ringkasan-${a.id}`}>
          <span className="break-words">{isLong ? `${sum.lines[0].slice(0, 180)}${sum.lines[0].length > 180 ? "…" : ""}${sum.lines.length > 1 ? ` (+${sum.lines.length - 1} perubahan lain)` : ""}` : sum.short}</span>
          {isLong && (
            <button type="button" onClick={() => setDetailItem(a)} data-testid={`act-detail-${a.id}`}
              className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] font-semibold text-[#0B1D3A] hover:text-[#FF5A1F] underline underline-offset-2">
              <FileSearch size={10} /> Lihat Detail
            </button>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="bg-white border border-slate-200" data-testid="riwayat-aktivitas-tab">
      <div className="px-4 py-2.5 border-b border-slate-200 flex items-center gap-2 flex-wrap">
        <History size={14} className="text-[#0B1D3A]" />
        <h3 className="text-sm font-semibold text-[#0B1D3A]">Riwayat Aktivitas</h3>
        <span className="text-[10px] text-slate-400 tabular-nums" data-testid="act-total">{total.toLocaleString("id-ID")} entri · read-only · aktivitas massal digabung (audit asli tetap utuh)</span>
      </div>

      {/* Filter: tindakan, pengguna, tanggal */}
      <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2 flex-wrap" data-testid="act-filter-bar">
        <select value={fAction} onChange={(e) => { setFAction(e.target.value); setPage(1); }} data-testid="act-filter-action"
          className="border border-slate-300 py-1.5 px-2 text-xs bg-white">
          <option value="">Semua Tindakan</option>
          {facets.actions.map((a) => <option key={a} value={a}>{ACTION_LABEL[a] || a}</option>)}
        </select>
        <select value={fActor} onChange={(e) => { setFActor(e.target.value); setPage(1); }} data-testid="act-filter-actor"
          className="border border-slate-300 py-1.5 px-2 text-xs bg-white">
          <option value="">Semua Pengguna</option>
          {facets.actors.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <div className="flex items-center gap-1 text-[11px] text-slate-500">
          <span>Tanggal</span>
          <input type="date" value={fFrom} onChange={(e) => { setFFrom(e.target.value); setPage(1); }}
            data-testid="act-filter-from" className="border border-slate-300 py-1 px-1.5 text-xs bg-white" />
          <span>–</span>
          <input type="date" value={fTo} onChange={(e) => { setFTo(e.target.value); setPage(1); }}
            data-testid="act-filter-to" className="border border-slate-300 py-1 px-1.5 text-xs bg-white" />
        </div>
        {hasFilter && (
          <button type="button" onClick={resetFilters} data-testid="act-filter-reset"
            className="inline-flex items-center gap-1 text-[10px] text-slate-500 hover:text-[#FF5A1F] underline uppercase tracking-wider">
            <X size={11} /> Reset
          </button>
        )}
      </div>

      {loading && items.length === 0 ? (
        <div className="p-6 text-center text-[11px] text-slate-400" data-testid="act-loading"><Loader2 size={14} className="inline animate-spin mr-1.5" />Memuat riwayat…</div>
      ) : items.length === 0 ? (
        <div className="p-6 text-center text-[11px] text-slate-400" data-testid="act-empty">
          {hasFilter ? "Tidak ada aktivitas yang cocok dengan filter." : "Belum ada aktivitas terekam untuk project ini."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[860px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">{["Waktu", "Pengguna", "Sumber", "Tindakan", "Ringkasan Perubahan"].map((h) => <th key={h} className="px-3 py-2 overline text-slate-600">{h}</th>)}</tr>
            </thead>
            <tbody data-testid="act-tbody" className={loading ? "opacity-50" : ""}>
              {grouped.map((g) => {
                if (g.items.length < 2) return renderActivityRow(g.items[0]);
                const rep = g.items[0];
                const gkey = rep.id;
                const open = expandedGroups.has(gkey);
                const repSum = summarize(rep);
                const tFrom = g.items[g.items.length - 1]._ts || g.items[g.items.length - 1].at || g.items[g.items.length - 1].created_at;
                const tTo = rep._ts || rep.at || rep.created_at;
                return (
                  <Fragment key={gkey}>
                    <tr data-testid={`act-group-${rep.id}`} className="border-b border-slate-100 bg-slate-50/70 hover:bg-slate-50">
                      <td className="px-3 py-2 tabular-nums text-slate-500 whitespace-nowrap">{fmtWaktu(tFrom)}<br />{fmtWaktu(tTo)}</td>
                      <td className="px-3 py-2 text-slate-700">{rep.actor_name || "—"}</td>
                      <td className="px-3 py-2 text-slate-600">{KIND_LABEL[rep.kind] || KIND_LABEL[rep.category] || rep.source || "Project"}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-1.5 py-0.5 text-[9px] font-semibold border ${ACTION_TONE[rep.action] || "bg-sky-50 border-sky-300 text-sky-700"}`}>
                          {ACTION_LABEL[rep.action] || rep.action || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-600 max-w-[380px]" data-testid={`act-group-summary-${rep.id}`}>
                        <button type="button" onClick={() => toggleGroup(gkey)} data-testid={`act-group-toggle-${rep.id}`}
                          className="inline-flex items-center gap-1 font-semibold text-[#0B1D3A] hover:text-[#FF5A1F] text-left">
                          {open ? <ChevronDown size={12} className="shrink-0" /> : <ChevronRight size={12} className="shrink-0" />}
                          {g.items.length} record · {repSum.short}
                        </button>
                        <div className="text-[9px] text-slate-400 mt-0.5">Aktivitas massal digabung — audit asli tetap utuh; buka untuk detail per record.</div>
                      </td>
                    </tr>
                    {open && g.items.map((a) => renderActivityRow(a, true))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination — default 20 entri/halaman */}
      {total > 0 && (
        <div className="px-4 py-2.5 border-t border-slate-200 flex items-center justify-between gap-2 flex-wrap text-[11px] text-slate-500" data-testid="act-pagination">
          <div className="flex items-center gap-1.5">
            <span>Halaman</span>
            <button type="button" data-testid="act-page-prev" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-2 py-1 border border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50">‹</button>
            <span className="tabular-nums font-semibold text-[#0B1D3A]" data-testid="act-page-info">{page} / {totalPages}</span>
            <button type="button" data-testid="act-page-next" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="px-2 py-1 border border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50">›</button>
          </div>
          <div className="flex items-center gap-2">
            <span>Entri per halaman</span>
            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              data-testid="act-page-size" className="border border-slate-300 py-1 px-1.5 text-xs bg-white">
              {[10, 20, 50, 100].map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            <span className="tabular-nums">Menampilkan {((page - 1) * pageSize + 1).toLocaleString("id-ID")}–{Math.min(page * pageSize, total).toLocaleString("id-ID")} dari {total.toLocaleString("id-ID")}</span>
          </div>
        </div>
      )}

      {/* Dialog detail perubahan — kalimat lengkap; JSON teknis khusus admin */}
      {detailItem && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 overflow-y-auto py-10 px-4" data-testid="act-detail-dialog">
          <div className="bg-white border border-slate-300 w-full max-w-2xl shadow-xl">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-sm font-bold text-[#0B1D3A]">Detail Perubahan</div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  {fmtWaktu(detailItem._ts || detailItem.at || detailItem.created_at)} · {detailItem.actor_name || "—"} · {ACTION_LABEL[detailItem.action] || detailItem.action}
                </div>
              </div>
              <button type="button" onClick={() => setDetailItem(null)} data-testid="act-detail-close"
                className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100"><X size={16} /></button>
            </div>
            <div className="px-4 py-3 max-h-[60vh] overflow-y-auto">
              <ul className="space-y-1.5 text-xs text-slate-700" data-testid="act-detail-lines">
                {summarize(detailItem).lines.map((l, i) => (
                  <li key={i} className="flex items-start gap-2"><span className="text-[#0B1D3A] mt-0.5">•</span><span className="break-words">{l}</span></li>
                ))}
              </ul>
              {isAdminViewer && (
                <details className="mt-4 border border-slate-200" data-testid="act-detail-raw">
                  <summary className="px-3 py-2 text-[10px] uppercase tracking-wider font-bold text-slate-500 cursor-pointer hover:bg-slate-50">Data Teknis (khusus admin)</summary>
                  <pre className="px-3 py-2 text-[10px] text-slate-600 bg-slate-50 overflow-x-auto whitespace-pre-wrap">{JSON.stringify(detailItem, null, 2)}</pre>
                </details>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
